import { useContext, useEffect, useRef, useState } from 'react';
import styles from './TableColumnSelector.module.css';
import {
  useSortable,
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import {
  getVisibleLeftPinnedColumns,
  getVisibleRightPinnedColumns,
  TableColumn,
  TableContext,
} from './Table';
import Button from '../forms/Button/Button';
import IconColumns from '../icons/IconColumns';
import IconSelect from '../icons/IconSelect';
import IconDrag from '../icons/IconDrag';
import Checkbox from '../forms/Checkbox/Checkbox';
import Flyout from '../Flyout';

interface ColumnListItemProps<TData> {
  column: TableColumn<TData>;
  onChecked: () => void;
}

function ColumnListItem<TData>({ column, onChecked }: ColumnListItemProps<TData>) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.listItem} onClick={() => onChecked()}>
      <button
        {...attributes}
        {...listeners}
        className={styles.listDragHandle}
      >
        <div className={styles.iconDragContainer}>
          <IconDrag />
        </div>
      </button>
      <Checkbox value={column.visible} onChange={() => onChecked()} />
      <div>{column.title}</div>
    </div>
  );
}

interface ColummSelectorProps<TData> {
  columns: TableColumn<TData>[];
}

export function TableColumnSelector<TData>({ columns }: ColummSelectorProps<TData>) {
  const { tableState, setTableState } = useContext(TableContext);
  const [columnList, setColumnList] = useState<TableColumn<TData>[]>(
    tableState.columns.map(tsCol => {
      const matchCol = columns.find(column => column.id === tsCol.id);
      matchCol.visible = tsCol.visible; //Make sure we persist visiblity value from state profile.
      return matchCol;
    }).filter(col => !col.pinned)
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [showColumnList, setShowColumnList] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columnList.findIndex((item) => item.id === active.id);
      const newIndex = columnList.findIndex((item) => item.id === over.id);
      const updatedList = arrayMove(columnList, oldIndex, newIndex);
      const allColumns = [
        ...getVisibleLeftPinnedColumns(tableState.columns),
        ...updatedList,
        ...getVisibleRightPinnedColumns(tableState.columns),
      ] as TableColumn<TData>[];
      setColumnList(updatedList);

      const reOrderedCols = allColumns.map(col => tableState.columns.find(tsCol => tsCol.id === col.id));

      setTableState({
        ...tableState,
        columns: reOrderedCols,
      });
    }
  }

  const handleInputCheck = (index: number) => {
    const updatedList = columnList.map((item, itemIndex) => {
      if (itemIndex === index) {
        return {
          ...item,
          visible: !item.visible,
        };
      }
      return item;
    });
    setColumnList(updatedList);

    const allColumns = [
      ...getVisibleLeftPinnedColumns(tableState.columns),
      ...updatedList,
      ...getVisibleRightPinnedColumns(tableState.columns),
    ] as TableColumn<TData>[];

    const reOrderedCols = allColumns.map(col => {
      const foundTsCol = tableState.columns.find(tsCol => tsCol.id === col.id);

      return {
        ...foundTsCol,
        visible: col.visible,
      };
    });

    setTableState({
      ...tableState,
      columns: reOrderedCols,
    });
  };

  useEffect(() => {
    const handleClickOutsideColumnList = (e: MouseEvent) => {
      const outsideButton = buttonRef.current && !buttonRef.current.contains(e.target as Node);
      const outsideFlyout = flyoutRef.current && !flyoutRef.current.contains(e.target as Node);
      if (outsideButton && outsideFlyout) {
        setShowColumnList(false);
      }
    };

    if (window) {
      window.addEventListener('click', handleClickOutsideColumnList);
      return () => {
        window.removeEventListener('click', handleClickOutsideColumnList);
      };
    }
  }, []);

  return (
    <div className={styles.columnSelector} ref={buttonRef}>
      <Button type="secondary" onClick={() => setShowColumnList(!showColumnList)}>
        <IconColumns /> <span>Columns</span><IconSelect />
      </Button>
      {showColumnList ? (
        <Flyout className={styles.listWrapper} anchor='right' matchWidth={false} ref={flyoutRef}>
          <div className={styles.list}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={columnList}
                strategy={verticalListSortingStrategy}
              >
                {columnList.map((column, index) => (
                  <ColumnListItem key={column.id} column={column} onChecked={() => handleInputCheck(index)} />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </Flyout>) : null}
    </div>

  );
}
